/**
 * Analytics module for the FeedOptimizer app.
 * Pushes structured events to window.dataLayer for GTM to process.
 */

import { hasAdUserDataConsent } from './consent';
import { isE2eTestSession } from './e2eFlag';
export { isE2eTestSession };

// --- Types ---
type ConversionEvent =
  | 'signup_complete'
  // Feld C, AP2: `feed_connected` meldet seit dem Umbau AUSSCHLIESSLICH den
  // ersten Merchant-Center-Anschluss (count(*) > 0 ueber merchant_centers),
  // nicht mehr den OAuth-Erfolg. Der Name bleibt, damit der bestehende
  // GTM-Trigger weiterlaeuft. Einzige Aufrufstelle: useConversionMilestones.
  | 'feed_connected'
  // Konversionswert nach Kataloggroesse (US-Kampagnenstart 2026-10-21): erster
  // abgeschlossener Produkt-Sync des Kontos. `value` = USD-Monatslistenpreis des
  // benoetigten Bands (lib/catalogConversionValue.ts), `currency` 'USD', dazu
  // `catalog_products`, `band`, `flow`, `entry`. Einzige Aufrufstelle:
  // useCatalogSyncedMilestone.
  | 'catalog_synced'
  // Der bisherige OAuth-Erfolg geht nicht verloren, er heisst nur noch, was er
  // misst: der Handler hat das Google-Konto gespeichert. Ob dieses Konto ein
  // Merchant Center traegt, ist zu diesem Zeitpunkt unbekannt.
  | 'google_account_connected'
  | 'optimization_started'
  | 'optimization_complete'
  | 'checkout_initiated'
  | 'subscription_activated'
  | 'upgrade_modal_shown'
  | 'upgrade_modal_clicked'
  | 'upgrade_banner_shown'
  | 'upgrade_banner_clicked'
  | 'upgrade_success_modal_clicked'
  | 'post_fetch_recommendation_shown'
  | 'post_fetch_recommendation_clicked'
  | 'pricing_page_viewed'
  | 'pricing_plan_selected'
  | 'success_dialog_upsell_clicked'
  | 'usage_indicator_clicked'
  | 'feed_check_report_view'
  | 'feed_check_insight_view'
  | 'feed_check_upsell_click'
  // Kapitel 3 des Feed-Checks (Spec 2026-09-09): der Kunde hat sein Google-Ads-
  // Konto verbunden. `source` ist 'feed_check' oder 'settings'.
  | 'google_ads_connected'
  // Klick auf "Interesse melden" im Teaser des Ads-Kapitels. Felder:
  // share_without_ads (Prozent), products_total. Die Meldung selbst ist eine
  // Zeile in `feedback` (Telegram ueber den bestehenden Trigger).
  | 'ads_segments_interest_click'
  // N7 (Bericht 8.3, Defekt 6): die Flaechen, die der Hauptpfad-Schnitt neu
  // schreibt, bekommen ihre Aufrufstellen sofort mit. Ohne sie laesst sich die
  // Wirkung des Umbaus hinterher nicht beurteilen, und dieselben Dateien
  // muessten dafuer ein zweites Mal geoeffnet werden.
  | 'journey_step_click'
  /**
   * Die Uebergaben der Leiter (Spec 2026-09-16, Abschnitt 7): nach Sync,
   * Optimierung, Freigabe, Upload und Ads-Connect steht der naechste Schritt
   * als Knopf dort, wo der Nutzer gerade fertig geworden ist. Traegt `from`
   * (der abgeschlossene Schritt), `to` und `target`.
   *
   * Getrennt von `journey_step_click`, und das ist der Zweck: der Chip-Klick
   * ist eine NAVIGATION im Rahmen, die Uebergabe eine ANNAHME des angebotenen
   * naechsten Schritts. Unter einem Namen liesse sich hinterher nicht mehr
   * sagen, ob der Nutzer die Fuehrung angenommen oder sich selbst orientiert
   * hat -- genau die Frage, die dieser Umbau beantworten soll.
   */
  | 'journey_handoff_click'
  | 'approval_decision'
  // Letzter Schritt der Leiter. Gemeldet wird der ANGENOMMENE Upload (der
  // Server hat eingereiht), nicht der Klick — ein am Control-Guard oder am
  // widerrufenen Merchant-Center-Zugriff gescheiterter Versuch ist kein Upload.
  | 'upload_started'
  // Review-Pipeline (Spec 2026-09-05, D6): Klick auf den Deep-Link der In-App-
  // Karte. Gleicher Ereignisname und dieselben Felder (location, label) wie das
  // cta_click der Marketing-Seite, damit der bestehende GTM-Trigger greift.
  // Werte: location 'review_card', label 'review_omr' | 'review_g2'.
  | 'cta_click';

// --- Core ---

/** Wert des GA4-Ereignis-Parameters `traffic_type` fuer interne Konten (GA4-Datenfilter „Internal Traffic"). */
export const INTERNAL_TRAFFIC_TYPE = 'internal';

// Modul-Merker: nach einem Property-Push mit user_internal=true traegt jedes
// weitere Ereignis dieses Seitenaufbaus traffic_type=internal. GA4s
// Datenfilter arbeitet auf Ereignis-Parametern, nicht auf User-Properties;
// nur so bleiben interne Konten aus dem Google-Ads-Import heraus.
let internalTraffic = false;

function pushEvent(event: string, data?: Record<string, unknown>) {
  if (isE2eTestSession()) return;
  window.dataLayer = window.dataLayer || [];
  const payload: Record<string, unknown> = { event, ...data };
  if (internalTraffic) payload.traffic_type = INTERNAL_TRAFFIC_TYPE;
  window.dataLayer.push(payload);
}

// --- Public API ---

export function trackConversion(
  event: ConversionEvent,
  metadata?: Record<string, string | number>,
) {
  pushEvent(event, metadata);
  if (import.meta.env.DEV) {
    console.log('[conversion]', event, metadata);
  }
}

export function trackPageView(path: string) {
  pushEvent('page_view', { page_path: path });
}

/**
 * Pusht die User-Properties fuer GTM/GA4.
 *
 * `isInternal` spiegelt `profiles.is_internal` (Team-/Agentur-/E2E-Konten). Es
 * landet als `user_internal` im dataLayer und wird im GTM-Container ueber eine
 * Data-Layer-Variable auf eine GA4-User-Property gemappt, damit interne Konten
 * aus den Funnel-Reports gefiltert werden koennen (Bericht 8.2: die Zahlen
 * waren durch eigene Testkonten kontaminiert). Das Mapping im GTM-Container ist
 * ein manueller Schritt und passiert nicht hier.
 *
 * Ist das Flag unbekannt (`undefined`, z.B. Profil-Select fehlgeschlagen), fehlt
 * der Key im Objekt komplett — ein `user_internal: undefined` wuerde GTM als
 * leeren String verarbeiten und die Property in GA4 ueberschreiben.
 */
function pushUserPropertiesEvent(userId: string, plan?: string, isInternal?: boolean) {
  // Zuweisung, keine Einwegsperre (I1, Final-Review 2026-09-16): meldet sich
  // in einem Seitenaufbau erst ein internes und danach ein externes Konto
  // (Abmelden/Anmelden im selben Tab), muss der Merker mitwandern. Mit einer
  // reinen `if (isInternal === true)`-Sperre blieb er auf `true` stehen, und
  // die Primaerkonversion des externen Kontos trug faelschlich
  // traffic_type=internal -- GA4s Datenfilter haette sie verworfen. Fehlt das
  // Flag (`undefined`, z.B. Profil-Select gescheitert), faellt der Merker
  // ebenso auf `false` zurueck, wie es der fehlende `user_internal`-Key im
  // Objekt darunter schon tut.
  internalTraffic = isInternal === true;
  const properties: Record<string, unknown> = { user_id: userId, user_plan: plan };
  if (isInternal !== undefined) properties.user_internal = isInternal;
  pushEvent('user_properties_set', properties);
}

export function setUserProperties(userId: string, plan?: string, isInternal?: boolean) {
  pushUserPropertiesEvent(userId, plan, isInternal);
  // NACH dem Push, und ausserhalb des E2E-Kurzschlusses: `pushEvent` schluckt
  // den Push mit gesetztem fo-e2e-test-Flag, aber die Wartenden muessen
  // trotzdem weiterlaufen. Sonst haengt ein E2E-Lauf 3000 ms an jedem
  // Meilenstein.
  markUserPropertiesReady();
}

// --- Reihenfolge-Tor fuer Konversionsereignisse ---

/**
 * GA4 wendet eine spaeter gesetzte User-Property NICHT rueckwirkend auf ein
 * frueheres Ereignis an. Ein Konversionsereignis, das vor `user_properties_set`
 * im dataLayer liegt, traegt also kein `user_internal`, und interne Konten sind
 * live 3 von 3 der Testkonten. Wer ein Konversionsereignis meldet, wartet
 * deshalb erst hier.
 *
 * Der harte Timeout ist Pflicht, kein Komfort: `setUserProperties` haengt an
 * einem `profiles`-Select, und ein fehlgeschlagener Select darf ein
 * Konversionsereignis nie verschlucken. Nach Ablauf wird gemeldet, wenn auch
 * ohne Property. Eine fehlende Funnel-Eigenschaft ist verkraftbar, eine
 * fehlende Konversion nicht.
 */
export const USER_PROPERTIES_READY_TIMEOUT_MS = 3000;

let userPropertiesReady = false;
let userPropertiesWaiters: Array<() => void> = [];

function markUserPropertiesReady(): void {
  if (userPropertiesReady) return;
  userPropertiesReady = true;
  const waiters = userPropertiesWaiters;
  userPropertiesWaiters = [];
  for (const resolve of waiters) resolve();
}

export function whenUserPropertiesReady(): Promise<void> {
  // Bereits-erfuellt-Zustand: `setUserProperties` laeuft aus dem
  // SIGNED_IN-Zweig und ist beim Mount eines Hooks in aller Regel schon durch.
  // Ein reines Warten auf ein KUENFTIGES Ereignis wuerde hier dauerhaft in den
  // Timeout laufen.
  if (userPropertiesReady) return Promise.resolve();
  return new Promise<void>((resolve) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const finish = () => {
      if (settled) return;
      settled = true;
      if (timer !== undefined) clearTimeout(timer);
      resolve();
    };
    timer = setTimeout(finish, USER_PROPERTIES_READY_TIMEOUT_MS);
    userPropertiesWaiters.push(finish);
  });
}

/**
 * Tracks a GA4 ecommerce purchase event.
 * Uses the standard GA4 purchase schema so it appears in Monetization reports.
 */
export function trackPurchase(params: {
  transactionId: string;
  planTier: string;
  planName: string;
  value: number;
  currency: 'EUR' | 'USD';
  billingInterval: string;
}) {
  pushEvent('purchase', {
    transaction_id: params.transactionId,
    affiliation: 'stripe',
    value: params.value,
    currency: params.currency,
    items: [{
      item_id: params.planTier,
      item_name: params.planName,
      price: params.value,
      quantity: 1,
      item_category: params.billingInterval,
    }],
  });
  if (import.meta.env.DEV) {
    console.log('[purchase]', params);
  }
}

// Idempotenz je Seitenaufbau: SIGNED_IN feuert bei auth-js auch aus
// _recoverAndRefresh, zwei Tabs oder ein StrictMode-Doppelmount duerfen
// keinen zweiten Push erzeugen.
const gepushteEmails = new Set<string>();

async function sha256Hex(text: string): Promise<string> {
  const data = new TextEncoder().encode(text);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hash)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Enhanced Conversions: gehashte E-Mail fuer den Google-Tag in den dataLayer.
 *
 * Laeuft NUR bei erteilter Marketing-Einwilligung (`ad_user_data: granted`,
 * gelesen aus dem Domain-Cookie). Der Consent-Default ist `denied`, und ein
 * Push ohne Einwilligung waere ein Datenschutzverstoss, den kein Tag-Test
 * sieht: die Daten laegen im dataLayer, und der erste Handgriff, der
 * „User-provided data collection" in GA4 einschaltet, schickte sie los.
 *
 * Der Klartext verlaesst den Browser nie: SHA-256 der getrimmten
 * Kleinschreibung, Feldname `sha256_email_address` wie von Google erwartet.
 * Gibt true zurueck, wenn gepusht wurde; wirft nie.
 *
 * Zwei Aufrufstellen, beide fuehren den Waechter
 * `src/test/userDataConsentInvariant.test.ts` in seiner Allowlist: einmal aus
 * `contexts/AuthContext.tsx` ueber `pushConversionPrerequisites` beim
 * Anmelden, einmal direkt aus `components/settings/GeneralTab.tsx`
 * (`saveCookieConsent`) fuer eine Einwilligung, die erst NACH dem Anmelden
 * erteilt wird. Eine dritte Stelle gehoert in diese Allowlist, nicht daran
 * vorbei. Gegenstueck bei Widerruf: `clearUserDataForConversions()` unten.
 */
export async function pushUserDataIfConsented(email: string): Promise<boolean> {
  const normalized = (email ?? '').trim().toLowerCase();
  if (!normalized) return false;
  if (isE2eTestSession()) return false;
  if (!hasAdUserDataConsent()) return false;
  if (gepushteEmails.has(normalized)) return false;
  // Vor dem await markieren, nicht danach: zwei Aufrufe derselben E-Mail im
  // selben synchronen Turn bestehen sonst BEIDE die has()-Pruefung, bevor
  // einer von ihnen add() erreicht, und pushen zweimal. Ein gescheitertes
  // Hashen nimmt die Markierung im catch zurueck, damit ein Retry moeglich
  // bleibt.
  gepushteEmails.add(normalized);
  try {
    const hashHex = await sha256Hex(normalized);
    // Nach dem await noch einmal fragen (N1, Re-Review 18.09.): kommt der
    // Widerruf, waehrend das Hashen laeuft, hat `clearUserDataForConversions`
    // den Merker geleert und das Cookie steht auf false. Ohne diese Pruefung
    // landete ein `set_user_data` NACH dem `clear_user_data`, also ohne
    // Einwilligung. Der leere Merker ist dabei die Generationsmarke: er
    // unterscheidet "dieser Lauf ist noch der aktuelle" von "dazwischen wurde
    // widerrufen".
    if (!hasAdUserDataConsent() || !gepushteEmails.has(normalized)) return false;
    pushEvent('set_user_data', { user_data: { sha256_email_address: hashHex } });
    return true;
  } catch {
    gepushteEmails.delete(normalized);
    return false;
  }
}

/**
 * Gegenstueck zu `pushUserDataIfConsented`: raeumt die gehashte E-Mail aus dem
 * dataLayer, wenn die Marketing-Einwilligung im selben Seitenaufbau
 * widerrufen wird (M2, Final-Review 2026-09-16).
 *
 * GTM haelt den dataLayer kumulativ -- ein Widerruf entfernt einen vorher
 * gepushten Hash nicht von selbst, eine User-Provided-Data-Variable im Tag
 * laese ihn also weiter tragen. `user_data: null` ist Googles vorgesehener
 * Weg, eine vorher gesetzte User-Provided-Data zurueckzunehmen.
 *
 * Leert zugleich `gepushteEmails`: eine erneute Erteilung nach diesem
 * Widerruf ist ein neuer Push, kein unterdruecktes Duplikat.
 *
 * Traegt bewusst KEINE eigene `isE2eTestSession()`-Sperre -- das Loeschen ist
 * kein Tracking, sondern dessen Rueckabwicklung. `pushEvent` selbst prueft
 * das Flag ohnehin (siehe oben), ein E2E-Lauf pusht also trotzdem nichts;
 * `gepushteEmails` wird in diesem Fall dennoch geleert, was folgenlos ist,
 * weil ein E2E-Lauf gar nicht erst pusht.
 *
 * Einzige Aufrufstelle heute: `components/settings/GeneralTab.tsx`
 * (`saveCookieConsent`, `else`-Zweig bei `!state.marketing`). Der Waechter
 * `src/test/userDataConsentInvariant.test.ts` pinnt diese Funktion bewusst
 * NICHT: sie ist kein Push-Pfad fuer Nutzerdaten, eine weitere Aufrufstelle
 * kann nichts ohne Einwilligung verschicken. Gepinnt sind nur die
 * Aufrufstellen von `pushUserDataIfConsented`.
 */
export function clearUserDataForConversions(): void {
  pushEvent('clear_user_data', { user_data: null });
  gepushteEmails.clear();
}

/**
 * Die Voraussetzungen JEDES Konversionsereignisses, in fester Reihenfolge:
 * 1. `user_properties_set` (mit user_internal, sobald bekannt),
 * 2. `set_user_data` (nur mit Consent, siehe oben),
 * 3. Bereitschaft fuer `whenUserPropertiesReady()`.
 *
 * Die Bereitschaft faellt im `finally`: ein Fehler beim Hashen darf kein
 * Konversionsereignis verschlucken (eine fehlende Enhanced-Angabe ist
 * verkraftbar, eine fehlende Konversion nicht). Einzige Aufrufstelle:
 * `AuthContext.pushUserPropertiesWithInternalFlag`.
 */
export async function pushConversionPrerequisites(
  userId: string,
  isInternal: boolean | undefined,
  email: string | undefined,
): Promise<void> {
  try {
    pushUserPropertiesEvent(userId, undefined, isInternal);
    if (email) await pushUserDataIfConsented(email);
  } finally {
    markUserPropertiesReady();
  }
}
